/**
 * InBody capture, BUILD.md §7.1.
 *
 * Supports the InBody 570 sheet layout: eight whole-body metrics plus segmental
 * lean and fat for five sites. A sheet can be uploaded for parsing, and every
 * field remains manually overridable.
 *
 * The page is only reachable when the clinic has InBody enabled (§0.3); the API
 * returns 404 otherwise, which this screen surfaces plainly.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, fullName } from '../lib/format';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  LoadingRows,
  PageHeader,
  Pill,
  Table,
  Td,
  Th,
  inputClass,
} from '../components/ui';
import { IconUpload } from '../components/icons';

interface ScanRow {
  id: string;
  scanDate: string;
  isBaseline: boolean;
  weightLb: number | null;
  smmLb: number | null;
  bodyFatMassLb: number | null;
  percentBodyFat: number | null;
  leanBodyMassLb: number | null;
  totalBodyWaterLb: number | null;
  bmrKcal: number | null;
  visceralFatLevel: number | null;
  segLeanRa: number | null;
  segLeanLa: number | null;
  segLeanTrunk: number | null;
  segLeanRl: number | null;
  segLeanLl: number | null;
  segFatRa: number | null;
  segFatLa: number | null;
  segFatTrunk: number | null;
  segFatRl: number | null;
  segFatLl: number | null;
}

/** The eight whole-body metrics, in the order the InBody sheet prints them. */
const WHOLE_BODY: Array<{ key: keyof FormState; label: string; unit: string; step: string }> = [
  { key: 'weightLb', label: 'Weight', unit: 'lb', step: '0.1' },
  { key: 'smmLb', label: 'Skeletal muscle mass', unit: 'lb', step: '0.1' },
  { key: 'bodyFatMassLb', label: 'Body fat mass', unit: 'lb', step: '0.1' },
  { key: 'percentBodyFat', label: 'Percent body fat', unit: '%', step: '0.1' },
  { key: 'leanBodyMassLb', label: 'Lean body mass', unit: 'lb', step: '0.1' },
  { key: 'totalBodyWaterLb', label: 'Total body water', unit: 'lb', step: '0.1' },
  { key: 'bmrKcal', label: 'Basal metabolic rate', unit: 'kcal', step: '1' },
  { key: 'visceralFatLevel', label: 'Visceral fat level', unit: 'level', step: '1' },
];

const SEGMENTS: Array<{ lean: keyof FormState; fat: keyof FormState; label: string }> = [
  { lean: 'segLeanRa', fat: 'segFatRa', label: 'Right arm' },
  { lean: 'segLeanLa', fat: 'segFatLa', label: 'Left arm' },
  { lean: 'segLeanTrunk', fat: 'segFatTrunk', label: 'Trunk' },
  { lean: 'segLeanRl', fat: 'segFatRl', label: 'Right leg' },
  { lean: 'segLeanLl', fat: 'segFatLl', label: 'Left leg' },
];

type FormState = Record<string, string> & { scanDate: string };

const EMPTY_FORM: FormState = {
  scanDate: new Date().toISOString().slice(0, 10),
  weightLb: '', smmLb: '', bodyFatMassLb: '', percentBodyFat: '',
  leanBodyMassLb: '', totalBodyWaterLb: '', bmrKcal: '', visceralFatLevel: '',
  segLeanRa: '', segLeanLa: '', segLeanTrunk: '', segLeanRl: '', segLeanLl: '',
  segFatRa: '', segFatLa: '', segFatTrunk: '', segFatRl: '', segFatLl: '',
};

export function InBodyPage() {
  const { id: patientId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [isBaseline, setIsBaseline] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const patient = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () =>
      api.get<{ id: string; firstName: string; lastName: string; sexAtBirth: string | null }>(
        `/patients/${patientId}`,
      ),
    enabled: Boolean(patientId),
  });

  const scans = useQuery({
    queryKey: ['inbody', patientId],
    queryFn: () => api.get<{ data: ScanRow[] }>(`/inbody${qs({ patientId })}`),
    enabled: Boolean(patientId),
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      // Send multipart when a sheet is attached, JSON otherwise.
      if (file) {
        const body = new FormData();
        body.set('patientId', patientId!);
        body.set('scanDate', form.scanDate);
        body.set('isBaseline', String(isBaseline));
        for (const [key, value] of Object.entries(form)) {
          if (key !== 'scanDate' && value !== '') body.set(key, value);
        }
        body.set('file', file);
        return api.post<ScanRow>('/inbody', body as unknown as FormData);
      }

      const numeric: Record<string, number | null> = {};
      for (const [key, value] of Object.entries(form)) {
        if (key === 'scanDate') continue;
        numeric[key] = value === '' ? null : Number(value);
      }
      return api.post<ScanRow>('/inbody', {
        patientId,
        scanDate: form.scanDate,
        isBaseline,
        ...numeric,
      });
    },
    onSuccess: () => {
      setError(null);
      setMessage('Scan saved.');
      setForm({ ...EMPTY_FORM, scanDate: form.scanDate });
      setFile(null);
      setIsBaseline(false);
      void queryClient.invalidateQueries({ queryKey: ['inbody', patientId] });
      void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
    },
    onError: (caught) => {
      setMessage(null);
      setError(caught instanceof Error ? caught.message : 'Could not save the scan');
    },
  });

  // §0.3, the API 404s when the feature is off; say so rather than showing a
  // form that can never succeed.
  if (user && !user.tenant.inbodyEnabled) {
    return (
      <>
        <PageHeader title="Body composition" />
        <Card>
          <EmptyState
            title="Body composition is not enabled for this clinic"
            body="An administrator can turn it on in Settings. Reports will then include the body-composition section for patients who have a scan."
            action={
              <Link to="/settings">
                <Button variant="secondary">Open settings</Button>
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  if (patient.error) return <ErrorState error={patient.error} retry={() => void patient.refetch()} />;
  if (patient.isLoading || !patient.data) return <LoadingRows rows={8} />;

  const set = (key: string, value: string) => setForm({ ...form, [key]: value });

  const scanList = scans.data?.data ?? [];
  const latestScanId = scanList.length > 0 ? scanList[scanList.length - 1]!.id : null;

  return (
    <>
      <PageHeader
        title="Body composition"
        subtitle={`${fullName(patient.data)} · InBody 570 capture`}
        actions={
          <Link to={`/patients/${patientId}`}>
            <Button variant="ghost">Back to patient</Button>
          </Link>
        }
      />

      {(message || error) && (
        <div
          className={`mb-5 rounded-md2 px-4 py-3 text-sm ${
            error
              ? 'border border-red-200 bg-red-50 text-red-800'
              : 'border border-emerald-200 bg-emerald-50 text-emerald-800'
          }`}
        >
          {error ?? message}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            saveMutation.mutate();
          }}
          className="space-y-6"
        >
          <Card title="Scan">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Scan date" required>
                <input
                  type="date"
                  required
                  className={inputClass}
                  value={form.scanDate}
                  onChange={(event) => set('scanDate', event.target.value)}
                />
              </Field>
              <Field
                label="Baseline"
                hint="The scan every later report compares against. Only one per patient."
              >
                <label className="flex items-center gap-2 h-[38px]">
                  <input
                    type="checkbox"
                    checked={isBaseline}
                    onChange={(event) => setIsBaseline(event.target.checked)}
                    className="h-4 w-4 rounded border-ink/10"
                  />
                  <span className="text-sm text-ink">Mark as baseline</span>
                </label>
              </Field>
            </div>

            <div className="mt-4">
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                className="hidden"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
              <div className="flex items-center gap-3 flex-wrap">
                <Button variant="secondary" size="sm" onClick={() => fileInput.current?.click()}>
                  <IconUpload size={14} />
                  {file ? 'Change sheet' : 'Attach InBody sheet'}
                </Button>
                {file && <span className="text-xs text-ink-muted">{file.name}</span>}
              </div>
              <p className="mt-2 text-[11px] text-ink-muted">
                Optional. The sheet is stored with the scan; values you type below always win over
                anything parsed from it.
              </p>
            </div>
          </Card>

          <Card title="Whole-body metrics">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {WHOLE_BODY.map((metric) => (
                <Field key={String(metric.key)} label={`${metric.label} (${metric.unit})`}>
                  <input
                    type="number"
                    step={metric.step}
                    className={inputClass}
                    value={form[String(metric.key)] ?? ''}
                    onChange={(event) => set(String(metric.key), event.target.value)}
                  />
                </Field>
              ))}
            </div>
          </Card>

          <Card title="Segmental analysis" padded={false}>
            <p className="px-5 pt-4 text-xs text-ink-muted">
              Each site adjusts only the matching limb in the report's figure, a stronger right arm
              changes the right arm and nothing else.
            </p>
            <Table>
              <thead>
                <tr>
                  <Th>Site</Th>
                  <Th align="right">Lean mass (lb)</Th>
                  <Th align="right">Fat mass (lb)</Th>
                </tr>
              </thead>
              <tbody>
                {SEGMENTS.map((segment) => (
                  <tr key={segment.label}>
                    <Td className="font-medium text-ink">{segment.label}</Td>
                    <Td align="right">
                      <input
                        type="number"
                        step="0.1"
                        aria-label={`${segment.label} lean mass`}
                        className="w-24 text-right rounded border border-ink/10 px-2 py-1 text-sm
                          focus:outline-none focus:ring-2 focus:ring-[var(--brand-accent)]"
                        value={form[String(segment.lean)] ?? ''}
                        onChange={(event) => set(String(segment.lean), event.target.value)}
                      />
                    </Td>
                    <Td align="right">
                      <input
                        type="number"
                        step="0.1"
                        aria-label={`${segment.label} fat mass`}
                        className="w-24 text-right rounded border border-ink/10 px-2 py-1 text-sm
                          focus:outline-none focus:ring-2 focus:ring-[var(--brand-accent)]"
                        value={form[String(segment.fat)] ?? ''}
                        onChange={(event) => set(String(segment.fat), event.target.value)}
                      />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <Button type="submit" loading={saveMutation.isPending}>
            Save scan
          </Button>
        </form>

        {/* --- History + live figure preview -------------------------- */}
        <div className="space-y-6">
          <Card title="Scans on file" padded={false}>
            {scans.isLoading ? (
              <div className="p-5"><LoadingRows rows={3} /></div>
            ) : !scans.data || scans.data.data.length === 0 ? (
              <EmptyState title="No scans yet" />
            ) : (
              <ul className="divide-y divide-slate-100">
                {[...scans.data.data].reverse().map((scan) => (
                  <li key={scan.id} className="px-5 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-ink">
                        {formatDate(scan.scanDate)}
                      </span>
                      {scan.isBaseline && (
                        <Pill className="bg-ink/5 text-ink-muted ring-ink/10">baseline</Pill>
                      )}
                    </div>
                    <p className="text-[11px] text-ink-muted mt-1">
                      {[
                        scan.weightLb !== null && `${scan.weightLb.toFixed(1)} lb`,
                        scan.percentBodyFat !== null && `${scan.percentBodyFat.toFixed(1)}% BF`,
                        scan.smmLb !== null && `${scan.smmLb.toFixed(1)} lb SMM`,
                        scan.visceralFatLevel !== null && `visceral ${scan.visceralFatLevel}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {latestScanId && <FigurePreview scanId={latestScanId} />}
        </div>
      </div>
    </>
  );
}

/**
 * Live figure preview.
 *
 * Fetched through the API client rather than an `<img src>`, because the endpoint
 * requires the bearer token, a plain image request would carry no auth header
 * and 401.
 */
function FigurePreview({ scanId }: { scanId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['inbody', 'figure', scanId],
    queryFn: async () => {
      const response = await api.get<Response>(`/inbody/${scanId}/figure.svg`, { raw: true });
      return response.text();
    },
  });

  return (
    <Card title="Figure preview">
      <p className="text-[11px] text-ink-muted mb-3">
        Generated deterministically from the most recent scan. This is the same figure the report
        prints.
      </p>
      {isLoading ? (
        <LoadingRows rows={3} />
      ) : error ? (
        <p className="text-xs text-ink-muted text-center py-6">Preview unavailable.</p>
      ) : (
        <div
          className="flex justify-center [&>svg]:h-64 [&>svg]:w-auto"
          dangerouslySetInnerHTML={{ __html: data ?? '' }}
        />
      )}
    </Card>
  );
}
